import React from "react";
import { fromJS } from "immutable";
import { Switch, Text, Tooltip } from "@showitinc/showit-wc";

import Input from "../../../base/Input";
import AppConstants from "../../../../constants/AppConstants";
import HelpTooltip from "../../../../constants/HelpTooltips";
import CornerStyleProperties from "../../../../components/CornerStyleProperties";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import {
  GALLERY_COLUMNS,
  GALLERY_ENFORCE_ORDER,
  GALLERY_FULL_SCREEN_BACKGROUND_COLOR,
  GALLERY_FULL_SCREEN_CONTROL_COLOR,
  GALLERY_IMAGE_BORDER,
  GALLERY_IMAGE_BORDER_COLOR,
  GALLERY_IMAGE_BORDER_RADIUS,
  GALLERY_IMAGE_BORDER_WIDTH,
  GALLERY_IMAGE_PADDING,
} from "../../../../constants/MergeConstants";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import { COLOR_PICKER } from "../../../../constants/InputConstants.mjs";
import { isPropValueMixed } from "../../../../utils/InheritanceUtils";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import LeftOffset from "../../../../components/base/LeftOffset";

const { CORNER_STYLE_OPTION_NONE, UPDATE_ACTION_DELETE, UPDATE_ACTION_SET } = AppConstants;

const defaults = fromJS({
  gallery: {
    imgPad: 0,
    cols: 3,
    enforceOrder: false,
    imgBorder: false,
    fs: {
      bgc: "#000000",
      cc: "#FFFFFF",
    },
  },
});

const imgBorderDefaults = fromJS({
  w: 0,
  rad: CORNER_STYLE_OPTION_NONE,
});

const BorderControls = ({ disabled, element, layout, onPropUpdate, preparedElement }) => {
  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const gatheredProps = defaults.mergeDeep(
    gatherProps(preparedElement, layout, [GALLERY_IMAGE_BORDER], true)
  );

  const initialBorderProps = gatheredProps.getIn(GALLERY_IMAGE_BORDER.split("."));

  const synced = isSynced(GALLERY_IMAGE_BORDER);
  const { isSyncMixed, canMerge, isMixed } = prepareInputUtils(
    layout,
    preparedElement,
    gatheredProps
  );
  const hasBorder =
    !isMixed(GALLERY_IMAGE_BORDER) && !isSyncMixed(GALLERY_IMAGE_BORDER)
      ? initialBorderProps !== false
      : false;
  const borderControls = [];

  const _onBorderChange = (propInfo) => {
    const { value } = propInfo;
    if (value === false) {
      propInfo.action = UPDATE_ACTION_DELETE;
    } else if (value === true) {
      propInfo.value = {
        w: 0,
        c: "colors-2",
      };
      propInfo.action = UPDATE_ACTION_SET;
    }

    onPropUpdate(propInfo);
  };

  if (hasBorder) {
    const borderProps = imgBorderDefaults.mergeDeep(initialBorderProps);

    const separatePath = (path) => path.split(".").slice(-1)[0];
    const getValue = (path) => borderProps.get(separatePath(path));
    const prepChange = (path) => [path, getValue(path)];
    const getChildPropName = (propName) => getPropName(propName, synced);

    const { onChange } = initFormatter(onPropUpdate, getChildPropName);

    borderControls.push(
      <LeftOffset key="gallery-border-controls">
        <Input
          disabled={disabled}
          min="0"
          key="border-width"
          label="Border Width"
          onSIBeforeChange={onChange(...prepChange(GALLERY_IMAGE_BORDER_WIDTH))}
          onSIInput={onChange(...prepChange(GALLERY_IMAGE_BORDER_WIDTH), false)}
          reserveContext
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={getValue(GALLERY_IMAGE_BORDER_WIDTH)}
        />
        <Input
          disabled={disabled}
          key="border-color"
          inputType={COLOR_PICKER}
          color={getValue(GALLERY_IMAGE_BORDER_COLOR)}
          label="Border Color"
          onSIBeforeChange={onChange(...prepChange(GALLERY_IMAGE_BORDER_COLOR))}
          onSIChange={onChange(...prepChange(GALLERY_IMAGE_BORDER_COLOR))}
          onSIInput={onChange(...prepChange(GALLERY_IMAGE_BORDER_COLOR), false)}
        />
        <CornerStyleProperties
          key="border-radius"
          isLocked={disabled}
          propName={getPropName(GALLERY_IMAGE_BORDER_RADIUS, synced)}
          value={borderProps.get("rad")}
          onPropUpdate={onPropUpdate}
        />
      </LeftOffset>
    );
  }

  const { onChange: onBorderSwitch } = initFormatter(_onBorderChange, getPropName);

  return (
    <SplitMergeWrapper
      layout={layout}
      mixed={isSyncMixed(GALLERY_IMAGE_BORDER)}
      sync={synced}
      toggleSync={() => toggleSyncedProp(GALLERY_IMAGE_BORDER)}
    >
      <Switch
        context={canMerge(GALLERY_IMAGE_BORDER)}
        disabled={disabled}
        checked={hasBorder}
        label="Enable Border"
        onSIChange={onBorderSwitch(GALLERY_IMAGE_BORDER, hasBorder)}
      />
      {borderControls}
    </SplitMergeWrapper>
  );
};

const GalleryTiledProperties = ({ disabled, element, layout, onPropUpdate, preparedElement }) => {
  const _onPropChange = (propInfo) => onPropUpdate && onPropUpdate(propInfo);

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    _onPropChange,
    layout
  );

  const elemProps = defaults.mergeDeep(
    gatherProps(
      preparedElement,
      layout,
      [
        GALLERY_IMAGE_PADDING,
        GALLERY_COLUMNS,
        GALLERY_ENFORCE_ORDER,
        GALLERY_FULL_SCREEN_BACKGROUND_COLOR,
        GALLERY_FULL_SCREEN_CONTROL_COLOR,
      ],
      true
    )
  );

  const { onChange } = initFormatter(onPropUpdate, getPropName);

  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];

  const { canMerge, prepareValue, isSyncMixed, isMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  const enforceOrder =
    !isSyncMixed(GALLERY_ENFORCE_ORDER) && !isMixed(GALLERY_ENFORCE_ORDER)
      ? getValue(GALLERY_ENFORCE_ORDER) === true
      : false;
  // Booleans need an explicit set; without an action the store deletes them.
  const withSet = (update) =>
    typeof update.value === "boolean" ? { ...update, action: UPDATE_ACTION_SET } : update;
  const onBooleanChange = (updates) =>
    _onPropChange(Array.isArray(updates) ? updates.map(withSet) : withSet(updates));
  const { onChange: onEnforceOrderChange } = initFormatter(onBooleanChange, getPropName);
  const { toggleSyncedProp: toggleEnforceOrderSync } = elementSyncUtils(
    element,
    onBooleanChange,
    layout
  );

  return (
    <>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_COLUMNS)}
        sync={isSynced(GALLERY_COLUMNS)}
        toggleSync={() => toggleSyncedProp(GALLERY_COLUMNS)}
      >
        <Input
          context={canMerge(GALLERY_COLUMNS)}
          disabled={disabled}
          label="Columns"
          min="1"
          onSIBeforeChange={onChange(...prepChange(GALLERY_COLUMNS))}
          onSIInput={onChange(...prepChange(GALLERY_COLUMNS), false)}
          type={INPUT_TYPE_NUMBER}
          value={prepareValue(GALLERY_COLUMNS)}
        />
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_ENFORCE_ORDER)}
        sync={isSynced(GALLERY_ENFORCE_ORDER)}
        toggleSync={() => toggleEnforceOrderSync(GALLERY_ENFORCE_ORDER)}
      >
        <Tooltip position="bottom-left">
          <Switch
            context={canMerge(GALLERY_ENFORCE_ORDER)}
            checked={enforceOrder}
            data-testid="gallery-enforce-order"
            disabled={disabled}
            label="Keep images in order"
            onSIChange={onEnforceOrderChange(GALLERY_ENFORCE_ORDER, enforceOrder)}
          />
          <Text slot="content">{HelpTooltip["gallery-enforce-order"]?.t}</Text>
        </Tooltip>
      </SplitMergeWrapper>
      <Text className="panel-subheading">Image Settings</Text>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_IMAGE_PADDING)}
        sync={isSynced(GALLERY_IMAGE_PADDING)}
        toggleSync={() => toggleSyncedProp(GALLERY_IMAGE_PADDING)}
      >
        <Input
          context={canMerge(GALLERY_IMAGE_PADDING)}
          disabled={disabled}
          label="Padding"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_IMAGE_PADDING))}
          onSIInput={onChange(...prepChange(GALLERY_IMAGE_PADDING), false)}
          step="2"
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_IMAGE_PADDING)}
        />
      </SplitMergeWrapper>
      <BorderControls
        disabled={disabled}
        element={element}
        onPropUpdate={_onPropChange}
        layout={layout}
        preparedElement={preparedElement}
      />

      <Text className="panel-subheading">Full Screen View</Text>

      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_FULL_SCREEN_BACKGROUND_COLOR)}
        sync={isSynced(GALLERY_FULL_SCREEN_BACKGROUND_COLOR)}
        toggleSync={() => toggleSyncedProp(GALLERY_FULL_SCREEN_BACKGROUND_COLOR)}
      >
        <Input
          color={prepareValue(GALLERY_FULL_SCREEN_BACKGROUND_COLOR)}
          context={canMerge(GALLERY_FULL_SCREEN_BACKGROUND_COLOR)}
          disabled={disabled}
          inputType={COLOR_PICKER}
          label="BG Color"
          onSIBeforeChange={onChange(...prepChange(GALLERY_FULL_SCREEN_BACKGROUND_COLOR))}
          onSIChange={onChange(...prepChange(GALLERY_FULL_SCREEN_BACKGROUND_COLOR))}
          onSIInput={onChange(...prepChange(GALLERY_FULL_SCREEN_BACKGROUND_COLOR), false)}
        />
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_FULL_SCREEN_CONTROL_COLOR)}
        sync={isSynced(GALLERY_FULL_SCREEN_CONTROL_COLOR)}
        toggleSync={() => toggleSyncedProp(GALLERY_FULL_SCREEN_CONTROL_COLOR)}
      >
        <Input
          color={prepareValue(GALLERY_FULL_SCREEN_CONTROL_COLOR)}
          context={canMerge(GALLERY_FULL_SCREEN_CONTROL_COLOR)}
          disabled={disabled}
          inputType={COLOR_PICKER}
          label="Controls Color"
          onSIBeforeChange={onChange(...prepChange(GALLERY_FULL_SCREEN_CONTROL_COLOR))}
          onSIChange={onChange(...prepChange(GALLERY_FULL_SCREEN_CONTROL_COLOR))}
          onSIInput={onChange(...prepChange(GALLERY_FULL_SCREEN_CONTROL_COLOR), false)}
        />
      </SplitMergeWrapper>
    </>
  );
};

export default GalleryTiledProperties;
